import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
import ProjectEdit from 'src/pages/dashboard/ProjectEdit';
import MediaRewards from 'src/pages/dashboard/MediaRewards';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <AuthGuard>
              <Login />
            </AuthGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        {
          path: 'login-unprotected',
          element: <Login />,
        },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },

        { path: 'media-cooperation', element: <MediaCooperation /> },
        { path: 'projects', element: <Projects /> },
        { path: 'project/new', element: <ProjectCreate /> },
        { path: 'project/edit/:projectId', element: <ProjectEdit /> },

        { path: 'project/:projectId/report', element: <ProjectReport /> },

        { path: 'project/:projectId/config-layout', element: <ProjectConfigLayout /> },

        { path: 'project/:projectId/config-profile', element: <ProjectConfigProfile /> },

        { path: 'project/:projectId/config-sidebar', element: <ProjectConfigSidebar /> },
        { path: 'project/:projectId/config-navbar', element: <ProjectConfigNavbar /> },
        { path: 'project/:projectId/config-footer', element: <ProjectConfigFooter /> },

        { path: 'project/:projectId/:appSectionKeyOrder/:appSectionId/feeds', element: <ProjectFeedList /> },
        { path: 'project/:projectId/:appSectionKeyOrder/:appSectionId/feeds/new-feed', element: <ProjectFeedCreate /> },
        { path: 'project/:projectId/:appSectionKeyOrder/:appSectionId/feeds/:feedId/edit-feed', element: <ProjectFeedEdit /> },

        { path: 'project/:projectId/:appSectionKeyOrder/:appSectionId/feeds/:feedId/feed-items', element: <FeedItems /> },

        { path: 'project/:projectId/wheels', element: <WheelList /> },

        { path: 'project/:projectId/app-sections', element: <AppSectionList /> },

        { path: 'project/:projectId/app-sections/:sectionKeyOrder/bannersList', element: <AppSectionBannersList /> },

        { path: 'project/:projectId/profile-reward', element: <ProfileRewardList /> },

        { path: 'project/:projectId/game-entry-fee-rewards', element: <GameEntryFeeRewards /> },

        { path: 'project/:projectId/wheels/:wheelId/wheel-items', element: <WheelItemList /> },

        { path: 'project/:projectId/media-social-player-list', element: <MediaSocialPlayerList /> },

        { path: 'project/:projectId/subscribe-package-list', element: <SubscribePackageList /> },

        { path: 'content-manager', element: <ContentManager /> },
        { path: 'content-manager/new-content', element: <ContentCreateForm /> },
        { path: 'content-manager/contents/:mediaId/manage-content/', element: <ManageContent /> },

        { path: 'daily-traffic-report', element: <DailyTrafficReport /> },
        { path: 'traffic-report', element: <TrafficReport /> },
        // games
        { path: 'games-manager/games-list', element: <GamesManager /> },

        { path: 'games-manager/:gameId/manage-game', element: <GameManagement /> },

        // duel
        { path: 'project/:projectId/duels', element: <DuelList /> },
        { path: 'project/:projectId/leaderboards', element: <LeaderboardList /> },

        { path: 'project/:projectId/notification-group-type-list/', element: <NotificationBannerTypeList /> },
        { path: 'project/:projectId/notification-group-type-list/:typeId', element: <NotificationBannerList /> },

        { path: 'project/:projectId/game-fee-list', element: <GameFeeList /> },

        { path: 'project/:projectId/exchange-list', element: <ExchangeList /> },

        { path: 'project/:projectId/avatar-list', element: <AvatarList /> },
        { path: 'project/:projectId/mediaRewards', element: <MediaRewards /> },

        { path: 'game-playlist', element: <GamePlayList /> },

        { path: 'game-playlist/:gamePlaylistId/game-join-list', element: <GameJoinList /> },

        { path: 'media-group-type-list', element: <MediaGroupTypeList /> },
        { path: 'media-group-list/:mediaGroupTypeId', element: <MediaGroupList /> },
        { path: 'media-group-list/:mediaGroupTypeId/:mediaGroupId/media-group-item-list', element: <MediaGroupItemList /> },

        { path: 'my-profile', element: <MyProfile /> },

        { path: 'user-list', element: <UserList /> },
        { path: 'user-list/:userId/edit-user', element: <UserEdit /> },

        { path: 'landing-list', element: <LandingList /> },

        { path: 'ads-list', element: <AdsList /> },
        { path: 'ads-list/new-ads', element: <AdsCreate /> },
        { path: 'ads-list/:adsId/edit-ads', element: <AdsEdit /> },

        { path: 'project/:projectId/transaction-package-list', element: <TransactionPackageList /> },

        { path: 'project/:projectId/rtmp-list', element: <RtmpList /> },

        { path: 'project/:projectId/rtmp-app-list', element: <RtmpAppList /> },

        { path: 'project/:projectId/comment-list', element: <CommentList /> },

        { path: 'project/:projectId/notification-popup-list', element: <NotificationPopupList /> },

        { path: 'upload-to-server/list', element: <UploadToServerList /> },
        { path: 'upload-to-server/list/new', element: <UploadToServerCreate /> },

        { path: 'metadata-management', element: <ManageMetaData /> },

        // -------------------------------------------------------------------------------

        {
          path: 'financial-management',
          children: [
            { element: <Navigate to="/dashboard/financial-management/reports" replace />, index: true },

            { path: 'reports', element: <FinancialReports /> },

            { path: 'gateway-list', element: <GatewayList /> },

            { path: 'gateway-profile-list', element: <GatewayProfileList /> },
            { path: 'gateway-profile-list/new-gateway-profile', element: <GatewayProfileCreate /> },
            { path: 'gateway-profile-list/:gatewayProfileId/edit-gateway-profile', element: <GatewayProfileEdit /> },

            { path: 'gateway-join-profile-list', element: <GatewayJoinProfileList /> },
          ],
        },

        // -------------------------------------------------------------------------------

        {
          path: 'common-sections',
          children: [
            { element: <Navigate to="/dashboard/common-sections/transaction-amount-type-list" replace />, index: true },
            { path: 'transaction-amount-type-list', element: <TransactionAmountTypeList /> },
            { path: 'package-type-list', element: <SubscribePackageTypeList /> },
            { path: 'package-position-list', element: <SubscribePackagePositionList /> },
            { path: 'notification-style-list', element: <NotificationStyleList /> },
            { path: 'product-type', element: <ProductType /> },
          ],
        },

        // -------------------------------------------------------------------------------

        {
          path: 'footballop',
          children: [
            { element: <Navigate to="/dashboard/footballop/game-card-list" replace />, index: true },

            { path: 'game-card-list', element: <GameCardList /> },

            { path: 'game-planet-list', element: <GamePlanetList /> },
            { path: 'game-planet-list/new-game-planet', element: <GamePlanetCreate /> },
            { path: 'game-planet-list/:gamePlanetId/edit-game-planet', element: <GamePlanetEdit /> },

            { path: 'gift-code-list', element: <GiftCodeList /> },
            { path: 'gift-code-list/new-gift-code', element: <GiftCodeCreate /> },
            { path: 'gift-code-list/:giftCodeId/edit-gift-code', element: <GiftCodeEdit /> },
          ],
        },

        // -------------------------------------------------------------------------------

        {
          path: 'social-manager',
          children: [
            { element: <Navigate to="/dashboard/social-manager/account-list" replace />, index: true },
            { path: 'account-list', element: <SocialManagerAccountList /> },
            { path: 'channel-list', element: <SocialManagerChannelList /> },
            { path: 'content-list', element: <SocialManagerContentList /> },
          ],
        },

        // -------------------------------------------------------------------------------

        { path: 'app-version-control-list', element: <ManageAppVersionControl /> },

        { path: 'home', element: <AdminWorkspace /> },
        { path: 'app', element: <GeneralApp /> },
        { path: 'ecommerce', element: <GeneralEcommerce /> },
        { path: 'analytics', element: <GeneralAnalytics /> },
        { path: 'banking', element: <GeneralBanking /> },
        { path: 'booking', element: <GeneralBooking /> },
        {
          path: 'e-commerce',
          children: [
            { element: <Navigate to="/dashboard/e-commerce/shop" replace />, index: true },
            { path: 'shop', element: <EcommerceShop /> },
            { path: 'product/:name', element: <EcommerceProductDetails /> },
            { path: 'list', element: <EcommerceProductList /> },
            { path: 'product/new', element: <EcommerceProductCreate /> },
            { path: 'product/:name/edit', element: <EcommerceProductCreate /> },
            { path: 'checkout', element: <EcommerceCheckout /> },
          ],
        },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/profile" replace />, index: true },
            { path: 'profile', element: <UserProfile /> },
            { path: 'cards', element: <UserCards /> },
            { path: 'list', element: <FUserList /> },
            { path: 'new', element: <UserCreate /> },
            { path: ':name/edit', element: <UserCreate /> },
            { path: 'account', element: <UserAccount /> },
          ],
        },
        {
          path: 'invoice',
          children: [
            { element: <Navigate to="/dashboard/invoice/list" replace />, index: true },
            { path: 'list', element: <InvoiceList /> },
            { path: ':id', element: <InvoiceDetails /> },
            { path: ':id/edit', element: <InvoiceEdit /> },
            { path: 'new', element: <InvoiceCreate /> },
          ],
        },
        {
          path: 'blog',
          children: [
            { element: <Navigate to="/dashboard/blog/posts" replace />, index: true },
            { path: 'posts', element: <BlogPosts /> },
            { path: 'post/:title', element: <BlogPost /> },
            { path: 'new', element: <BlogNewPost /> },
          ],
        },
        {
          path: 'mail',
          children: [
            { element: <Navigate to="/dashboard/mail/all" replace />, index: true },
            { path: 'label/:customLabel', element: <Mail /> },
            { path: 'label/:customLabel/:mailId', element: <Mail /> },
            { path: ':systemLabel', element: <Mail /> },
            { path: ':systemLabel/:mailId', element: <Mail /> },
          ],
        },
        {
          path: 'chat',
          children: [
            { element: <Chat />, index: true },
            { path: 'new', element: <Chat /> },
            { path: ':conversationKey', element: <Chat /> },
          ],
        },
        { path: 'calendar', element: <Calendar /> },
        { path: 'kanban', element: <Kanban /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <Navigate to="/dashboard/projects" />,
      // element: <MainLayout />,
      // children: [
      //   { element: <HomePage />, index: true },
      //   { path: 'about-us', element: <About /> },
      //   { path: 'contact-us', element: <Contact /> },
      //   { path: 'faqs', element: <Faqs /> },
      // ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));

const AdminWorkspace = Loadable(lazy(() => import('../pages/dashboard/AdminWorkspace')));

const Projects = Loadable(lazy(() => import('../pages/dashboard/Projects')));
const ProjectFeedList = Loadable(lazy(() => import('../pages/dashboard/ProjectFeedList')));
const ProjectCreate = Loadable(lazy(() => import('../pages/dashboard/ProjectCreate')));

const ProjectConfigLayout = Loadable(lazy(() => import('../pages/dashboard/ProjectConfigLayout')));

const ProjectConfigProfile = Loadable(lazy(() => import('../pages/dashboard/ProjectConfigProfile')));

const ProjectConfigSidebar = Loadable(lazy(() => import('../pages/dashboard/ProjectConfigSidebar')));
const ProjectConfigNavbar = Loadable(lazy(() => import('../pages/dashboard/ProjectConfigNavbar')));
const ProjectConfigFooter = Loadable(lazy(() => import('../pages/dashboard/ProjectConfigFooter')));
const ProjectFeedCreate = Loadable(lazy(() => import('../pages/dashboard/ProjectFeedCreate')));
const ProjectFeedEdit = Loadable(lazy(() => import('../pages/dashboard/ProjectFeedEdit')));

const FeedItems = Loadable(lazy(() => import('../pages/dashboard/FeedItems')));

const ContentManager = Loadable(lazy(() => import('../pages/dashboard/ContentManager')));
const ContentCreateForm = Loadable(lazy(() => import('../pages/dashboard/ContentCreateForm')));
const ManageContent = Loadable(lazy(() => import('../pages/dashboard/ManageContent')));
const DailyTrafficReport = Loadable(lazy(() => import('../pages/dashboard/DailyTrafficReport')));
const TrafficReport = Loadable(lazy(() => import('../pages/dashboard/TrafficReport')));

// GAMES
const GameManagement = Loadable(lazy(() => import('../pages/dashboard/GameManagement')));
const GamesManager = Loadable(lazy(() => import('../pages/dashboard/GamesManager')));

// DUELS
const DuelList = Loadable(lazy(() => import('../pages/dashboard/DuelList')));

// Leaderboard
const LeaderboardList = Loadable(lazy(() => import('../pages/dashboard/Leaderboard')));

// GAME ENTRY FEE REWARD
const GameEntryFeeRewards = Loadable(lazy(() => import('../pages/dashboard/GameEntryFeeRewards')));

// NotificationBanners
const NotificationBannerTypeList = Loadable(lazy(() => import('../pages/dashboard/NotificationBanner/NotificationBannerTypes')));
const NotificationBannerList = Loadable(lazy(() => import('../pages/dashboard/NotificationBanner/NotificationBannerList')));

// GameJoinApplicationPlaylist
const GamePlayList = Loadable(lazy(() => import('../pages/dashboard/GamePlayList')));

// GameJoinApplication
const GameJoinList = Loadable(lazy(() => import('../pages/dashboard/GameJoinList')));

// GameEntryFee
const GameFeeList = Loadable(lazy(() => import('../pages/dashboard/GameFeeList')));

// Exchange
const ExchangeList = Loadable(lazy(() => import('../pages/dashboard/ExchangeList')));

// Avatar List
const AvatarList = Loadable(lazy(() => import('../pages/dashboard/AvatarList')));

// Media Group Type List
const MediaGroupTypeList = Loadable(lazy(() => import('../pages/dashboard/MediaGroupTypeList')));

// MediaGroup
const MediaGroupList = Loadable(lazy(() => import('../pages/dashboard/MediaGroupList')));

// MediaGroupItem
const MediaGroupItemList = Loadable(lazy(() => import('../pages/dashboard/MediaGroupItemList')));

// Wheel

const WheelList = Loadable(lazy(() => import('../pages/dashboard/WheelList')));

// WheelItem

const WheelItemList = Loadable(lazy(() => import('../pages/dashboard/WheelItemList')));

// MediaSocialPlayer

const MediaSocialPlayerList = Loadable(lazy(() => import('../pages/dashboard/MediaSocialPlayerList')));

// MediaSocialPlayer

const SubscribePackageList = Loadable(lazy(() => import('../pages/dashboard/SubscribePackageList')));

const TransactionAmountTypeList = Loadable(lazy(() => import('../pages/dashboard/TransactionAmountTypeList')));
const SubscribePackageTypeList = Loadable(lazy(() => import('../pages/dashboard/SubscribePackageTypeList')));
const SubscribePackagePositionList = Loadable(lazy(() => import('../pages/dashboard/SubscribePackagePositionList')));
const NotificationStyleList = Loadable(lazy(() => import('../pages/dashboard/NotificationStyleList')));
const ProductType = Loadable(lazy(() => import('../pages/dashboard/ProductType')));
const NotificationPopupList = Loadable(lazy(() => import('../pages/dashboard/NotificationPopupList')));

// MyProfile
const MyProfile = Loadable(lazy(() => import('../pages/dashboard/MyProfile')));

// USERS
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserEdit = Loadable(lazy(() => import('../pages/dashboard/UserEdit')));

// Advertising

const AdsList = Loadable(lazy(() => import('../pages/dashboard/AdsList')));
const AdsCreate = Loadable(lazy(() => import('../pages/dashboard/AdsCreate')));
const AdsEdit = Loadable(lazy(() => import('../pages/dashboard/AdsEdit')));

// Gateway

const FinancialReports = Loadable(lazy(() => import('../pages/dashboard/FinancialReports')));

const GatewayList = Loadable(lazy(() => import('../pages/dashboard/GatewayList')));

const GatewayProfileList = Loadable(lazy(() => import('../pages/dashboard/GatewayProfileList')));
const GatewayProfileCreate = Loadable(lazy(() => import('../pages/dashboard/GatewayProfileCreate')));
const GatewayProfileEdit = Loadable(lazy(() => import('../pages/dashboard/GatewayProfileEdit')));

const GatewayJoinProfileList = Loadable(lazy(() => import('../pages/dashboard/GatewayJoinProfileList')));

// Footballop

const GameCardList = Loadable(lazy(() => import('../pages/dashboard/GameCardList')));

const GamePlanetList = Loadable(lazy(() => import('../pages/dashboard/GamePlanetList')));
const GamePlanetCreate = Loadable(lazy(() => import('../pages/dashboard/GamePlanetCreate')));
const GamePlanetEdit = Loadable(lazy(() => import('../pages/dashboard/GamePlanetEdit')));

const GiftCodeList = Loadable(lazy(() => import('../pages/dashboard/GiftCodeList')));
const GiftCodeCreate = Loadable(lazy(() => import('../pages/dashboard/GiftCodeCreate')));
const GiftCodeEdit = Loadable(lazy(() => import('../pages/dashboard/GiftCodeEdit')));

// TransactionPackages

const TransactionPackageList = Loadable(lazy(() => import('../pages/dashboard/TransactionPackageList')));

// RTMP

const RtmpList = Loadable(lazy(() => import('../pages/dashboard/RtmpList')));

// RTMP App

const RtmpAppList = Loadable(lazy(() => import('../pages/dashboard/RtmpAppList')));

// Comment List

const CommentList = Loadable(lazy(() => import('../pages/dashboard/CommentList')));

// Landings

const LandingList = Loadable(lazy(() => import('../pages/dashboard/LandingList')));

// Upload To Server

const UploadToServerList = Loadable(lazy(() => import('../pages/dashboard/UploadToServerList')));
const UploadToServerCreate = Loadable(lazy(() => import('../pages/dashboard/UploadToServerCreate')));

// App Sections

const AppSectionList = Loadable(lazy(() => import('../pages/dashboard/appSectionList')));

const AppSectionBannersList = Loadable(lazy(() => import('../pages/dashboard/AppSectionBannersList')));

// Profile Reward List

const ProfileRewardList = Loadable(lazy(() => import('../pages/dashboard/ProfileRewardList')));

// Manage MetaData

const ManageMetaData = Loadable(lazy(() => import('../pages/dashboard/ManageMetaData')));

// Manage App Version Control

const ManageAppVersionControl = Loadable(lazy(() => import('../pages/dashboard/AppVersionControlList')));

// Project Report

// const ProjectReport = Loadable(lazy(() => import('../pages/dashboard/ProjectReportClone')));
const ProjectReport = Loadable(lazy(() => import('../pages/dashboard/ProjectReport')));

// Social Manager

const SocialManagerAccountList = Loadable(lazy(() => import('../pages/dashboard/SocialManagerAccountList')));
const SocialManagerChannelList = Loadable(lazy(() => import('../pages/dashboard/SocialManagerChannelList')));
const SocialManagerContentList = Loadable(lazy(() => import('../pages/dashboard/SocialManagerContentList')));

// ECOMMERCE
const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')));
const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));

// INVOICE
const InvoiceList = Loadable(lazy(() => import('../pages/dashboard/InvoiceList')));
const InvoiceDetails = Loadable(lazy(() => import('../pages/dashboard/InvoiceDetails')));
const InvoiceCreate = Loadable(lazy(() => import('../pages/dashboard/InvoiceCreate')));
const InvoiceEdit = Loadable(lazy(() => import('../pages/dashboard/InvoiceEdit')));

// BLOG
const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));

// USER
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const FUserList = Loadable(lazy(() => import('../pages/dashboard/FUserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));

// APP
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));

// MAIN
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
const MediaCooperation = Loadable(lazy(() => import('../pages/dashboard/MediaCooperation')));
